/**
 * The three kinds of source a case's answer key names, and how an option of each reads.
 */

import type { DatasetExpectedSources, DatasetSourceState } from '@/types/simulation-dataset.types';

export type SourceKind = keyof DatasetExpectedSources;

export interface SourceOption {
	kind: SourceKind;
	id: string;
	name: string;
}

export const SOURCE_KIND_LABEL: Record<SourceKind, string> = {
	sopIds: 'SOP',
	workflowIds: 'Workflow',
	knowledgeIds: 'Knowledge'
};

const SOURCE_STATE_NOTE: Record<Exclude<DatasetSourceState, 'active'>, string> = {
	off: 'switched off',
	deleted: 'deleted'
};

/** What a source's chip notes about its state; nothing while it is switched on or its state is not known. */
export const sourceStateNote = (state: DatasetSourceState | undefined): string | null =>
	state && state !== 'active' ? SOURCE_STATE_NOTE[state] : null;

/** An id with no name to show, shortened. */
const shortId = (id: string): string => (id.length > 10 ? `${id.slice(0, 8)}…` : id);

const KINDS: SourceKind[] = ['sopIds', 'workflowIds', 'knowledgeIds'];

/** Every source a list names, each with its name, or its id shortened when the name is not known. */
export const sourceOptionsOf = (list: DatasetExpectedSources, names: ReadonlyMap<string, string>): SourceOption[] =>
	KINDS.flatMap((kind) => list[kind].map((id) => ({ kind, id, name: names.get(id) ?? shortId(id) })));

/** The sources with one more of its kind. */
export const addSource = (sources: DatasetExpectedSources, option: Pick<SourceOption, 'kind' | 'id'>): DatasetExpectedSources => ({
	...sources,
	[option.kind]: [...sources[option.kind], option.id]
});
